import { createHash, randomUUID } from "node:crypto";
import { chmod, mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { join, resolve } from "node:path";
import { isBinaryDistribution } from "../runtime/distribution";
import { shouldUseDevelopmentCredentialFile } from "./development-credential-store";
import {
  deleteMacOsNativeKeychainToken,
  getMacOsKeychainRetryAfterMs,
  readMacOsNativeKeychainToken,
  storeMacOsNativeKeychainToken,
} from "./macos-native-keychain";
import { openMeldRootDir } from "./paths";

const TOKEN_STORE_KEYCHAIN_SERVICE = "sh.openmeld.auth.token";
const INSECURE_FALLBACK_ENV_KEY = "OPENMELD_AUTH_ALLOW_INSECURE_FILE_TOKEN";
const FORCE_INSECURE_STORE_ENV_KEY = "OPENMELD_AUTH_FORCE_INSECURE_FILE_TOKEN";
const MAC_OS_KEYCHAIN_NONINTERACTIVE_UNAVAILABLE_MESSAGE =
  "macOS Keychain access is unavailable in this OpenMeld distribution. " +
  "OpenMeld will not open an authorization window from an unattended process. " +
  "Run setup or login with the current packaged OpenMeld CLI.";
const COMMAND_MAX_BUFFER_BYTES = 1024 * 1024;
const COMMAND_TIMEOUT_MS = 30_000;
const TOKEN_FILE_KEY_PATTERN = /^[a-f0-9]{64}$/u;
const COMMAND_ERROR_CODE_PATTERN = /^E[A-Z0-9_]{1,63}$/u;
const EXEC_FILE_BASE_OPTIONS = {
  windowsHide: true,
  maxBuffer: COMMAND_MAX_BUFFER_BYTES,
  timeout: COMMAND_TIMEOUT_MS,
} as const;
let insecureFallbackWarningShown = false;
let macOsPrivateStorageWarningShown = false;

type MacOsTokenRef = {
  provider: "macos-keychain";
  service: string;
  account: string;
};

type WindowsDpapiTokenRef = {
  provider: "windows-dpapi";
  key: string;
};

type InsecureFileTokenRef = {
  provider: "insecure-file";
  key: string;
};

export type StoredTokenRef =
  | MacOsTokenRef
  | WindowsDpapiTokenRef
  | InsecureFileTokenRef;

export type AccessTokenReadResult =
  | {
      accessToken: string;
      status: "found";
    }
  | {
      status: "missing";
    }
  | {
      errorMessage: string;
      retryAfterMs?: number;
      status: "unavailable";
    };

type StoreTokenInput = {
  accessToken: string;
  authBaseUrl: string;
  clientId: string;
  unique?: boolean;
};

export async function storeAccessToken(
  input: StoreTokenInput
): Promise<StoredTokenRef> {
  const key = buildTokenKey({
    authBaseUrl: input.authBaseUrl,
    clientId: input.clientId,
  });
  const storageKey = input.unique ? buildUniqueTokenFileKey(key) : key;

  if (shouldForceInsecureStore()) {
    await setInsecureFileToken({
      key: storageKey,
      accessToken: input.accessToken,
    });
    return {
      provider: "insecure-file",
      key: storageKey,
    };
  }

  if (await shouldUseDevelopmentCredentialFile(input.authBaseUrl)) {
    await setInsecureFileToken({
      key: storageKey,
      accessToken: input.accessToken,
    });
    console.warn(
      "[openmeld auth] Local development sign-in uses an unencrypted file restricted to your OS user in OPENMELD_HOME. Use test accounts only. Rebuilds keep this sign-in; use OPENMELD_DEV_CREDENTIAL_STORE=system and a separate home for system Keychain acceptance."
    );
    return { provider: "insecure-file", key: storageKey };
  }

  if (process.platform === "darwin") {
    try {
      const account = buildUniqueMacOsAccount(key);
      if (shouldUseNativeMacOsKeychain()) {
        await storeMacOsNativeKeychainToken({
          account,
          service: TOKEN_STORE_KEYCHAIN_SERVICE,
          accessToken: input.accessToken,
        });
      } else {
        throw new Error(MAC_OS_KEYCHAIN_NONINTERACTIVE_UNAVAILABLE_MESSAGE);
      }
      return {
        provider: "macos-keychain",
        service: TOKEN_STORE_KEYCHAIN_SERVICE,
        account,
      };
    } catch (error) {
      if (allowInsecureFallback()) {
        await setInsecureFileToken({
          key: storageKey,
          accessToken: input.accessToken,
        });
        return {
          provider: "insecure-file",
          key: storageKey,
        };
      }
      const privateKey = buildUniqueTokenFileKey(key);
      await setInsecureFileToken({
        key: privateKey,
        accessToken: input.accessToken,
      });
      warnMacOsPrivateStorageFallback(error);
      return { provider: "insecure-file", key: privateKey };
    }
  }

  if (process.platform === "win32") {
    try {
      await setWindowsDpapiToken({
        key: storageKey,
        accessToken: input.accessToken,
      });
      return {
        provider: "windows-dpapi",
        key: storageKey,
      };
    } catch (error) {
      if (allowInsecureFallback()) {
        await setInsecureFileToken({
          key: storageKey,
          accessToken: input.accessToken,
        });
        return {
          provider: "insecure-file",
          key: storageKey,
        };
      }
      throw new Error(
        `failed to store access token via Windows DPAPI: ${toErrorMessage(error)}`,
        { cause: error }
      );
    }
  }

  if (allowInsecureFallback()) {
    await setInsecureFileToken({
      key: storageKey,
      accessToken: input.accessToken,
    });
    return {
      provider: "insecure-file",
      key: storageKey,
    };
  }

  throw new Error(
    `secure token storage is unavailable on platform "${process.platform}". ` +
      `Use ${INSECURE_FALLBACK_ENV_KEY}=1 only for local testing fallback.`
  );
}

export async function readAccessToken(
  ref: StoredTokenRef
): Promise<AccessTokenReadResult> {
  try {
    if (ref.provider === "macos-keychain") {
      if (!shouldUseNativeMacOsKeychain()) {
        throw new Error(MAC_OS_KEYCHAIN_NONINTERACTIVE_UNAVAILABLE_MESSAGE);
      }
      const token = await readMacOsNativeKeychainToken(ref);
      return toAccessTokenReadResult(token);
    }
    if (ref.provider === "windows-dpapi") {
      return toAccessTokenReadResult(await getWindowsDpapiToken(ref));
    }
    if (ref.provider === "insecure-file") {
      return toAccessTokenReadResult(await getInsecureFileToken(ref));
    }
    return { status: "missing" };
  } catch (error) {
    return {
      errorMessage: toErrorMessage(error),
      ...(ref.provider === "macos-keychain"
        ? { retryAfterMs: getMacOsKeychainRetryAfterMs() }
        : {}),
      status: "unavailable",
    };
  }
}

export async function deleteAccessToken(ref: StoredTokenRef): Promise<void> {
  if (ref.provider === "macos-keychain") {
    if (!shouldUseNativeMacOsKeychain()) {
      throw new Error(MAC_OS_KEYCHAIN_NONINTERACTIVE_UNAVAILABLE_MESSAGE);
    }
    await deleteMacOsNativeKeychainToken(ref);
    return;
  }
  if (ref.provider === "windows-dpapi") {
    await deleteWindowsDpapiToken(ref);
    return;
  }
  if (ref.provider === "insecure-file") {
    await deleteInsecureFileToken(ref);
  }
}

export function isStoredTokenRef(value: unknown): value is StoredTokenRef {
  if (!(value && typeof value === "object")) {
    return false;
  }
  const record = value as Record<string, unknown>;
  const provider = normalizeString(record.provider);
  if (provider === "macos-keychain") {
    return Boolean(
      normalizeString(record.service) &&
        normalizeString(record.account) &&
        record.accessGroup === undefined
    );
  }
  if (provider === "windows-dpapi" || provider === "insecure-file") {
    return normalizeTokenFileKey(record.key) !== null;
  }
  return false;
}

function buildTokenKey(input: {
  authBaseUrl: string;
  clientId: string;
}): string {
  const rootDir = resolve(openMeldRootDir());
  return createHash("sha256")
    .update(`${input.clientId}\n${input.authBaseUrl}\n${rootDir}`)
    .digest("hex");
}

function buildUniqueMacOsAccount(key: string): string {
  return `openmeld-auth-${key}-${randomUUID()}`;
}

function buildUniqueTokenFileKey(key: string): string {
  return createHash("sha256").update(`${key}\n${randomUUID()}`).digest("hex");
}

function shouldUseNativeMacOsKeychain(): boolean {
  return isBinaryDistribution();
}

async function setWindowsDpapiToken(input: {
  key: string;
  accessToken: string;
}): Promise<void> {
  const encrypted = await runPowerShellScript(
    WINDOWS_DPAPI_ENCRYPT_SCRIPT,
    input.accessToken,
    input.key
  );
  await mkdir(tokenDirectoryPath(), { recursive: true });
  // NOTE: `mode: 0o600` is POSIX-only and Windows ignores it.
  // For `tokenDirectoryPath()` + `windowsDpapiTokenPath()` we rely on DPAPI
  // and Windows ACLs for equivalent protection.
  await writeFile(windowsDpapiTokenPath(input.key), `${encrypted.trim()}\n`, {
    encoding: "utf8",
    mode: 0o600,
  });
}

async function getWindowsDpapiToken(input: {
  key: string;
}): Promise<string | null> {
  const encrypted = await readFile(
    windowsDpapiTokenPath(input.key),
    "utf8"
  ).catch((error) => {
    if (isFileNotFoundError(error)) {
      return null;
    }
    throw error;
  });
  if (!encrypted) {
    return null;
  }
  return runPowerShellScript(
    WINDOWS_DPAPI_DECRYPT_SCRIPT,
    encrypted.trim(),
    input.key
  );
}

async function deleteWindowsDpapiToken(input: { key: string }): Promise<void> {
  await rm(windowsDpapiTokenPath(input.key), { force: true });
}

async function setInsecureFileToken(input: {
  key: string;
  accessToken: string;
}): Promise<void> {
  await ensurePrivateTokenDirectory();
  const path = insecureTokenPath(input.key);
  await writeFile(path, `${input.accessToken}\n`, {
    encoding: "utf8",
    mode: 0o600,
  });
  if (process.platform !== "win32") {
    await chmod(path, 0o600);
  }
}

async function getInsecureFileToken(input: {
  key: string;
}): Promise<string | null> {
  const raw = await readFile(insecureTokenPath(input.key), "utf8").catch(
    (error) => {
      if (isFileNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  );
  return raw ? raw.trim() : null;
}

async function deleteInsecureFileToken(input: { key: string }): Promise<void> {
  await rm(insecureTokenPath(input.key), { force: true });
}

function tokenDirectoryPath(): string {
  return join(openMeldRootDir(), "auth", "tokens");
}

function windowsDpapiTokenPath(key: string): string {
  return join(tokenDirectoryPath(), `${requireValidTokenFileKey(key)}.dpapi`);
}

function insecureTokenPath(key: string): string {
  return join(tokenDirectoryPath(), `${requireValidTokenFileKey(key)}.token`);
}

function allowInsecureFallback(): boolean {
  if (process.env[INSECURE_FALLBACK_ENV_KEY] === "1") {
    warnInsecureFallback("env");
    return true;
  }
  // Known downgrade behavior:
  // On platforms without native secure-store support, OpenMeld currently falls back
  // to local file token storage by default for compatibility.
  const fallbackEnabled = !isNativeSecureStorePlatform();
  if (fallbackEnabled) {
    warnInsecureFallback("platform");
  }
  return fallbackEnabled;
}

function warnMacOsPrivateStorageFallback(error: unknown): void {
  if (macOsPrivateStorageWarningShown) {
    return;
  }
  macOsPrivateStorageWarningShown = true;
  const storageExpectation = isBinaryDistribution()
    ? "This session stays in private local storage unless a later sign-in replaces it."
    : "This npm installation cannot use macOS Keychain. This session stays in private local storage.";
  console.warn(
    "[openmeld auth] macOS Keychain is unavailable. OpenMeld saved this " +
      "session in a private file protected for your macOS user account, so " +
      "setup can continue. " +
      `${storageExpectation} Details: ${toErrorMessage(error)}`
  );
}

function warnInsecureFallback(reason: "env" | "platform"): void {
  if (insecureFallbackWarningShown) {
    return;
  }
  insecureFallbackWarningShown = true;

  const reasonText =
    reason === "env"
      ? `enabled by ${INSECURE_FALLBACK_ENV_KEY}=1`
      : `enabled automatically on platform "${process.platform}"`;
  console.warn(
    `[openmeld auth] plaintext file token storage fallback is active (${reasonText}). ` +
      `Unset ${INSECURE_FALLBACK_ENV_KEY} to disable explicit opt-in, ` +
      "and prefer macOS or Windows for native secure token storage."
  );
}

function shouldForceInsecureStore(): boolean {
  return process.env[FORCE_INSECURE_STORE_ENV_KEY] === "1";
}

function isNativeSecureStorePlatform(): boolean {
  return process.platform === "darwin" || process.platform === "win32";
}

async function ensurePrivateTokenDirectory(): Promise<void> {
  const directory = tokenDirectoryPath();
  await mkdir(directory, { recursive: true, mode: 0o700 });
  if (process.platform !== "win32") {
    await chmod(directory, 0o700);
  }
}

async function runPowerShellScript(
  script: string,
  token: string,
  entropy: string
): Promise<string> {
  const payload = JSON.stringify({ token, entropy });
  const result = await runCommand(
    "powershell.exe",
    [
      "-NoProfile",
      "-NonInteractive",
      "-ExecutionPolicy",
      "Bypass",
      "-Command",
      script,
    ],
    payload
  );
  return result.stdout.trim();
}

async function runCommand(
  command: string,
  args: string[],
  stdinPayload: string
): Promise<{ stdout: string; stderr: string }> {
  try {
    const { execFile } = await import("node:child_process");
    const output = await new Promise<{ stdout: string; stderr: string }>(
      (resolve, reject) => {
        let stdout = "";
        let stderr = "";

        const child = execFile(command, args, { ...EXEC_FILE_BASE_OPTIONS });

        child.stdout?.setEncoding("utf8");
        child.stderr?.setEncoding("utf8");
        child.stdout?.on("data", (chunk: unknown) => {
          stdout += String(chunk);
        });
        child.stderr?.on("data", (chunk: unknown) => {
          stderr += String(chunk);
        });

        child.on("error", (error) => {
          reject(error);
        });

        child.on("close", (code) => {
          if (code === 0) {
            resolve({ stdout, stderr });
            return;
          }
          reject({ code, stderr });
        });

        child.stdin?.write(stdinPayload);
        child.stdin?.end();
      }
    );
    return output;
  } catch (error) {
    throw toCommandError(error, command);
  }
}

function toCommandError(
  error: unknown,
  command: string
): Error & { code: number | string | null } {
  const details =
    error && typeof error === "object"
      ? (error as { code?: unknown; stderr?: unknown })
      : {};
  let code: number | string | null = null;
  if (typeof details.code === "number" && Number.isInteger(details.code)) {
    code = details.code;
  } else if (
    typeof details.code === "string" &&
    COMMAND_ERROR_CODE_PATTERN.test(details.code)
  ) {
    code = details.code;
  }
  // Only fixed script markers may become diagnostics. stdout is a decrypted
  // token, and PowerShell/Node error messages can include input or command text.
  const marker =
    typeof details.stderr === "string" ? details.stderr.trim() : "";
  let reason = "PowerShell did not complete the credential operation";
  if (typeof code === "string") {
    reason = "PowerShell could not be started or executed";
  } else if (marker === "OPENMELD_DPAPI_ASSEMBLY_LOAD_FAILED") {
    reason = "Windows credential encryption could not be initialized";
  } else if (marker === "OPENMELD_DPAPI_OPERATION_FAILED") {
    reason = "Windows could not encrypt or decrypt the stored credential";
  }
  return Object.assign(
    new Error(
      `command failed (${command}); ${reason}; ${code === null ? "no exit code" : `code: ${code}`}`
    ),
    { code }
  );
}

function normalizeString(value: unknown): string {
  return String(value ?? "").trim();
}

function normalizeTokenFileKey(value: unknown): string | null {
  const normalized = normalizeString(value);
  if (!TOKEN_FILE_KEY_PATTERN.test(normalized)) {
    return null;
  }
  return normalized;
}

function requireValidTokenFileKey(value: unknown): string {
  const normalized = normalizeTokenFileKey(value);
  if (normalized === null) {
    throw new Error("stored token key is invalid");
  }
  return normalized;
}

function normalizeToken(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function toAccessTokenReadResult(value: string | null): AccessTokenReadResult {
  const accessToken = normalizeToken(value);
  if (!accessToken) {
    return { status: "missing" };
  }
  return {
    accessToken,
    status: "found",
  };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function isFileNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === "object" &&
      "code" in error &&
      error.code === "ENOENT"
  );
}

const WINDOWS_DPAPI_ENCRYPT_SCRIPT = [
  "$ErrorActionPreference='Stop'",
  "try { Add-Type -AssemblyName System.Security -ErrorAction Stop } catch { [Console]::Error.WriteLine('OPENMELD_DPAPI_ASSEMBLY_LOAD_FAILED'); exit 1 }",
  "try {",
  "$rawInput = [Console]::In.ReadToEnd()",
  "$payload = $rawInput | ConvertFrom-Json",
  "$plain=[System.Text.Encoding]::UTF8.GetBytes($payload.token)",
  "$entropy=[System.Text.Encoding]::UTF8.GetBytes($payload.entropy)",
  "$cipher=[System.Security.Cryptography.ProtectedData]::Protect($plain,$entropy,[System.Security.Cryptography.DataProtectionScope]::CurrentUser)",
  "[System.Convert]::ToBase64String($cipher)",
  "} catch { [Console]::Error.WriteLine('OPENMELD_DPAPI_OPERATION_FAILED'); exit 1 }",
].join(";");

const WINDOWS_DPAPI_DECRYPT_SCRIPT = [
  "$ErrorActionPreference='Stop'",
  "try { Add-Type -AssemblyName System.Security -ErrorAction Stop } catch { [Console]::Error.WriteLine('OPENMELD_DPAPI_ASSEMBLY_LOAD_FAILED'); exit 1 }",
  "try {",
  "$rawInput = [Console]::In.ReadToEnd()",
  "$payload = $rawInput | ConvertFrom-Json",
  "$cipher=[System.Convert]::FromBase64String($payload.token)",
  "$entropy=[System.Text.Encoding]::UTF8.GetBytes($payload.entropy)",
  "$plain=[System.Security.Cryptography.ProtectedData]::Unprotect($cipher,$entropy,[System.Security.Cryptography.DataProtectionScope]::CurrentUser)",
  "[System.Text.Encoding]::UTF8.GetString($plain)",
  "} catch { [Console]::Error.WriteLine('OPENMELD_DPAPI_OPERATION_FAILED'); exit 1 }",
].join(";");
